/**
 * Workflow Auto-Layout Utility
 *
 * Automatically positions workflow nodes in a readable layout.
 * Uses a simple tree/flow-based algorithm without external dependencies.
 */

import { Node, Edge } from 'reactflow';

/**
 * Layout configuration options
 */
export interface LayoutOptions {
  /** Direction of flow: 'TB' (top-bottom), 'LR' (left-right) */
  direction?: 'TB' | 'LR';
  /** Horizontal spacing between nodes */
  nodeSpacingX?: number;
  /** Vertical spacing between nodes */
  nodeSpacingY?: number;
  /** Default node width */
  nodeWidth?: number;
  /** Default node height */
  nodeHeight?: number;
  /** Starting X position */
  startX?: number;
  /** Starting Y position */
  startY?: number;
}

/**
 * Default layout options
 */
const DEFAULT_OPTIONS: Required<LayoutOptions> = {
  direction: 'TB',
  nodeSpacingX: 200,
  nodeSpacingY: 120,
  nodeWidth: 180,
  nodeHeight: 60,
  startX: 100,
  startY: 50,
};

/**
 * Node with computed layout information
 */
interface LayoutNode {
  id: string;
  level: number;
  order: number;
  children: string[];
  parents: string[];
}

/**
 * Build a graph structure from nodes and edges
 */
function buildGraph(nodes: Node[], edges: Edge[]): Map<string, LayoutNode> {
  const graph = new Map<string, LayoutNode>();

  // Initialize all nodes
  for (const node of nodes) {
    graph.set(node.id, {
      id: node.id,
      level: -1,
      order: 0,
      children: [],
      parents: [],
    });
  }

  // Build adjacency lists
  for (const edge of edges) {
    const source = graph.get(edge.source);
    const target = graph.get(edge.target);

    if (source && target) {
      source.children.push(edge.target);
      target.parents.push(edge.source);
    }
  }

  return graph;
}

/**
 * Find root nodes (nodes with no parents)
 */
function findRootNodes(graph: Map<string, LayoutNode>): string[] {
  const roots: string[] = [];

  for (const [id, node] of graph) {
    if (node.parents.length === 0) {
      roots.push(id);
    }
  }

  // If no roots found, use the first node as root
  if (roots.length === 0 && graph.size > 0) {
    roots.push(graph.keys().next().value);
  }

  return roots;
}

/**
 * Assign levels to nodes using BFS
 */
function assignLevels(graph: Map<string, LayoutNode>, roots: string[]): void {
  const visited = new Set<string>();
  const queue: Array<{ id: string; level: number }> = [];

  // Start with root nodes at level 0
  for (const root of roots) {
    queue.push({ id: root, level: 0 });
    const node = graph.get(root);
    if (node) {
      node.level = 0;
    }
  }

  while (queue.length > 0) {
    const { id, level } = queue.shift()!;

    if (visited.has(id)) {
      continue;
    }
    visited.add(id);

    const node = graph.get(id);
    if (!node) continue;

    // Process children
    for (const childId of node.children) {
      const child = graph.get(childId);
      if (child && !visited.has(childId)) {
        // Use max level to handle multiple parents
        child.level = Math.max(child.level, level + 1);
        queue.push({ id: childId, level: child.level });
      }
    }
  }

  // Assign level 0 to any unvisited nodes
  for (const [_, node] of graph) {
    if (node.level === -1) {
      node.level = 0;
    }
  }
}

/**
 * Group nodes by level
 */
function groupByLevel(graph: Map<string, LayoutNode>): Map<number, string[]> {
  const levels = new Map<number, string[]>();

  for (const [id, node] of graph) {
    const level = node.level;
    if (!levels.has(level)) {
      levels.set(level, []);
    }
    levels.get(level)!.push(id);
  }

  return levels;
}

/**
 * Calculate positions for nodes based on levels
 */
function calculatePositions(
  nodes: Node[],
  graph: Map<string, LayoutNode>,
  levels: Map<number, string[]>,
  options: Required<LayoutOptions>
): Node[] {
  const { direction, nodeSpacingX, nodeSpacingY, nodeWidth, nodeHeight, startX, startY } = options;

  // Sort levels
  const sortedLevels = Array.from(levels.keys()).sort((a, b) => a - b);

  // Calculate node positions by level
  const positionedNodes = new Map<string, { x: number; y: number }>();

  for (const level of sortedLevels) {
    const nodesAtLevel = levels.get(level) || [];
    const levelWidth = nodesAtLevel.length * (nodeWidth + nodeSpacingX) - nodeSpacingX;

    nodesAtLevel.forEach((nodeId, index) => {
      let x: number, y: number;

      if (direction === 'TB') {
        // Top to bottom layout
        x = startX + index * (nodeWidth + nodeSpacingX);
        y = startY + level * (nodeHeight + nodeSpacingY);
      } else {
        // Left to right layout
        x = startX + level * (nodeWidth + nodeSpacingX);
        y = startY + index * (nodeHeight + nodeSpacingY);
      }

      positionedNodes.set(nodeId, { x, y });
    });
  }

  // Apply positions to nodes
  return nodes.map((node) => {
    const position = positionedNodes.get(node.id);
    if (position) {
      return {
        ...node,
        position: {
          x: position.x,
          y: position.y,
        },
      };
    }
    return node;
  });
}

/**
 * Auto-layout workflow nodes
 *
 * @param nodes - Array of ReactFlow nodes
 * @param edges - Array of ReactFlow edges
 * @param options - Layout configuration options
 * @returns Nodes with updated positions
 */
export function autoLayoutWorkflow(
  nodes: Node[],
  edges: Edge[],
  options: LayoutOptions = {}
): Node[] {
  if (nodes.length === 0) {
    return nodes;
  }

  const config: Required<LayoutOptions> = {
    ...DEFAULT_OPTIONS,
    ...options,
  };

  // Build graph structure
  const graph = buildGraph(nodes, edges);

  // Find root nodes
  const roots = findRootNodes(graph);

  // Assign levels using BFS
  assignLevels(graph, roots);

  // Group nodes by level
  const levels = groupByLevel(graph);

  // Calculate and apply positions
  return calculatePositions(nodes, graph, levels, config);
}

/**
 * Create edges connecting nodes in sequence
 *
 * @param nodes - Array of nodes to connect
 * @returns Array of edges connecting the nodes
 */
export function createSequentialEdges(nodes: Node[]): Edge[] {
  const edges: Edge[] = [];

  for (let i = 0; i < nodes.length - 1; i++) {
    edges.push({
      id: `edge-${nodes[i].id}-${nodes[i + 1].id}`,
      source: nodes[i].id,
      target: nodes[i + 1].id,
      type: 'smoothstep',
    });
  }

  return edges;
}

/**
 * Position a single new node relative to existing nodes
 *
 * @param existingNodes - Current nodes in the workflow
 * @param existingEdges - Current edges in the workflow
 * @param newNodeId - ID of the new node
 * @param referenceNodeId - Optional ID of node to position relative to
 * @param position - Where to place relative to reference ('after', 'before', 'below')
 * @returns Suggested position for the new node
 */
export function suggestNodePosition(
  existingNodes: Node[],
  existingEdges: Edge[],
  referenceNodeId?: string,
  position: 'after' | 'before' | 'below' | 'parallel' = 'after'
): { x: number; y: number } {
  const nodeSpacingX = 200;
  const nodeSpacingY = 120;
  const nodeWidth = 180;
  const nodeHeight = 60;

  if (existingNodes.length === 0) {
    return { x: 100, y: 50 };
  }

  // Find reference node
  let referenceNode: Node | undefined;
  if (referenceNodeId) {
    referenceNode = existingNodes.find((n) => n.id === referenceNodeId);
  }

  // If no reference, use the last node
  if (!referenceNode) {
    referenceNode = existingNodes.reduce((latest, node) => {
      if (!latest) return node;
      return node.position.y > latest.position.y ? node : latest;
    }, existingNodes[0]);
  }

  const refX = referenceNode?.position.x || 100;
  const refY = referenceNode?.position.y || 50;

  switch (position) {
    case 'after':
      return { x: refX, y: refY + nodeHeight + nodeSpacingY };
    case 'before':
      return { x: refX, y: refY - nodeHeight - nodeSpacingY };
    case 'below':
      return { x: refX, y: refY + nodeHeight + nodeSpacingY };
    case 'parallel':
      return { x: refX + nodeWidth + nodeSpacingX, y: refY };
    default:
      return { x: refX, y: refY + nodeHeight + nodeSpacingY };
  }
}

/**
 * Center the viewport on the workflow
 *
 * @param nodes - Array of nodes
 * @returns Center point and bounds of the workflow
 */
export function calculateWorkflowBounds(nodes: Node[]): {
  center: { x: number; y: number };
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
} {
  if (nodes.length === 0) {
    return {
      center: { x: 0, y: 0 },
      bounds: { minX: 0, minY: 0, maxX: 0, maxY: 0 },
    };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const node of nodes) {
    minX = Math.min(minX, node.position.x);
    minY = Math.min(minY, node.position.y);
    maxX = Math.max(maxX, node.position.x + 180); // Assuming default node width
    maxY = Math.max(maxY, node.position.y + 60); // Assuming default node height
  }

  return {
    center: {
      x: (minX + maxX) / 2,
      y: (minY + maxY) / 2,
    },
    bounds: { minX, minY, maxX, maxY },
  };
}

export default autoLayoutWorkflow;
